import { Link } from "react-router-dom";
import { ArrowRight, Check, Sparkles, Volume2, Trophy, Brain, ShieldCheck, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { BlurReveal } from "@/components/effects/blur-reveal";
import { MagneticButton } from "@/components/aceternity/magnetic-button";
import { PageFrame } from "@/components/page-frame";
import { useAuth } from "@/lib/auth-context";

export default function Subscribe() {
  const { access } = useAuth();
  const hasLicence = access?.can_study === true;
  const licenseLabel = access?.profile.license_status === "license_personal"
    ? "Arabise Plus"
    : "Free";

  return (
    <PageFrame surface="marketing">
      <div className="relative pt-32 pb-24 px-6">
        <div aria-hidden className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] h-[600px] pointer-events-none"
          style={{ background: "radial-gradient(ellipse at top, rgba(201,169,106,0.20), transparent 60%)" }} />

        <div className="relative max-w-5xl mx-auto">
          <BlurReveal>
            <div className="text-center mb-12">
              <Badge variant="accent" className="mb-5"><Sparkles className="w-3 h-3" /> Pricing</Badge>
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight">
                Get full access to <span className="text-accent">Arabise</span>.
              </h1>
              <p className="mt-5 mx-auto max-w-xl text-fg-2 leading-relaxed">
                Unlimited quizzes, every GCSE topic, AI-marked translations, and audio pronunciation. Cancel anytime.
              </p>
              {access && (
                <div className="mt-5 inline-flex items-center gap-2 text-xs text-fg-2">
                  <ShieldCheck className="w-3 h-3 text-accent" />
                  Current plan: <span className="font-semibold text-fg">{licenseLabel}</span>
                </div>
              )}
            </div>
          </BlurReveal>

          <div className="grid gap-6">
            <BlurReveal>
              <article className="relative rounded-3xl border-2 border-accent-lo bg-bg-2 p-8 md:p-10 overflow-hidden max-w-2xl mx-auto w-full">
                <div aria-hidden className="absolute -right-12 -top-12 w-56 h-56 rounded-full bg-accent/15 blur-3xl" />

                <div className="relative">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs uppercase tracking-widest text-fg-3">Arabise Plus</div>
                      <div className="mt-2 flex items-baseline gap-2">
                        <span className="text-5xl md:text-6xl font-bold tracking-tight">£4.99</span>
                        <span className="text-fg-3">/ month</span>
                      </div>
                      <div className="mt-1 text-xs text-fg-2">Or £39.99/year — two months free</div>
                    </div>
                    {hasLicence
                      ? <Badge variant="success" className="self-start"><CheckCircle2 className="w-3 h-3" /> Active</Badge>
                      : <Badge variant="accent" className="self-start"><Sparkles className="w-3 h-3" /> Most popular</Badge>}
                  </div>

                  <Separator className="my-7" />

                  <ul className="space-y-3">
                    {PLUS_FEATURES.map(f => (
                      <li key={f} className="flex items-start gap-3 text-sm">
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-accent/15 text-accent shrink-0 mt-0.5">
                          <Check className="w-3 h-3" />
                        </span>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-8 flex flex-wrap gap-3">
                    {hasLicence ? (
                      <Button size="lg" asChild>
                        <Link to="/dashboard">Back to dashboard <ArrowRight className="w-4 h-4" /></Link>
                      </Button>
                    ) : (
                      <>
                        <MagneticButton>Subscribe via Stripe <ArrowRight className="w-4 h-4" /></MagneticButton>
                        <Button variant="outline" size="lg" asChild>
                          <Link to="/dashboard">Back to dashboard</Link>
                        </Button>
                      </>
                    )}
                  </div>

                  {!hasLicence && (
                    <div className="mt-6 text-xs text-fg-3 leading-relaxed">
                      Stripe checkout is being wired up. For now, message{" "}
                      <a href="mailto:support@arabise.co.uk" className="text-fg-2 hover:text-fg underline-offset-2 hover:underline">
                        support@arabise.co.uk
                      </a>{" "}
                      to activate your account manually.
                    </div>
                  )}
                </div>
              </article>
            </BlurReveal>
          </div>

          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {BENEFITS.map((b, i) => (
              <BlurReveal key={b.title} delay={i * 0.05}>
                <Card className="h-full">
                  <CardHeader>
                    <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-accent/10 border border-accent-lo text-accent mb-2">
                      <b.icon className="w-5 h-5" />
                    </div>
                    <CardTitle>{b.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-fg-2 leading-relaxed">{b.desc}</p>
                  </CardContent>
                </Card>
              </BlurReveal>
            ))}
          </div>

          <BlurReveal delay={0.2}>
            <p className="mt-14 text-center text-xs text-fg-3 max-w-md mx-auto leading-relaxed">
              By subscribing you agree to our <Link to="/terms" className="text-fg-2 hover:text-fg">Terms of Service</Link>.
              See our <Link to="/privacy" className="text-fg-2 hover:text-fg">Privacy Policy</Link> for payment data handling.
            </p>
          </BlurReveal>
        </div>
      </div>
    </PageFrame>
  );
}

const PLUS_FEATURES = [
  "Unlimited quizzes & sentence-builder rounds",
  "Every GCSE topic on the spec — AQA + Edexcel",
  "Translation practice with AI marking",
  "Audio pronunciation on every word",
  "Progress tracking + global leaderboard",
  "Daily streak rewards & bonus quests",
  "Cancel anytime — no commitment"
];

const BENEFITS = [
  { icon: Brain,       title: "AI marking",   desc: "Translation answers marked instantly with feedback." },
  { icon: Volume2,     title: "Live audio",   desc: "Native pronunciation, generated on every word." },
  { icon: Trophy,      title: "Leaderboards", desc: "Global rankings — see how you stack up." },
  { icon: ShieldCheck, title: "Secure",       desc: "MFA, encrypted at rest, GDPR-friendly by default." }
];
